import React, { useState, useMemo, useContext, useEffect, useRef } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useForm } from "react-hook-form";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  Award, Plus, Search, X, Calendar, Users, Globe,
  Play, Pause, Trash2, Eye, Clock, Target, Trophy, StopCircle
} from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import AuthContext from "../../context/AuthContext";
import nominationsService from "../../services/nominationsService";
import eventsService from "../../services/eventsService";
import budgetsService from "../../services/budgetsService";
import teamsService from "../../services/teamsService";
import organizationsService from "../../services/organizationsService";
import useCustomToast from "../../hooks/useCustomToast";
import { formatError } from "../../utils/errorHandler";
import AdminSectionCard from "./AdminSectionCard";
import { useTranslation, useFormat } from "../../i18n";

const NominationsSection = () => {
  const queryClient = useQueryClient();
  const toast = useCustomToast();
  const navigate = useNavigate();
  const { t } = useTranslation("admin");
  const fmt = useFormat();
  const { user } = useContext(AuthContext);
  const isSuperAdmin = user?.is_superuser;

  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showDetailsDrawer, setShowDetailsDrawer] = useState(false);
  const [selectedCampaign] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");

  const scopeLabels = {
    global: t("nominations.drawer.scope_global"),
    team: t("nominations.drawer.scope_team"),
  };

  // Fetch campaigns
  const { data: campaigns = [], isLoading: campaignsLoading } = useQuery({
    queryKey: ["campaigns", "admin"],
    queryFn: () => nominationsService.listAllCampaigns(),
  });

  // Filter campaigns by search query (client-side filtering for responsiveness)
  const filteredCampaigns = useMemo(() => {
    if (!searchQuery.trim()) return campaigns;
    const query = searchQuery.toLowerCase();
    return campaigns.filter(
      (c) =>
        c.name?.toLowerCase().includes(query) ||
        c.description?.toLowerCase().includes(query)
    );
  }, [campaigns, searchQuery]);

  // Fetch budgets
  const { data: budgets = [] } = useQuery({
    queryKey: ["budgets"],
    queryFn: () => budgetsService.list(),
  });

  // Fetch teams
  const { data: teams = [] } = useQuery({
    queryKey: ["teams"],
    queryFn: () => teamsService.list(),
  });

  // Fetch organizations
  const { data: organizations = [] } = useQuery({
    queryKey: ["organizations"],
    queryFn: () => organizationsService.list(),
  });

  const { data: myOrg } = useQuery({
    queryKey: ["organization", user?.organization_id],
    queryFn: () => organizationsService.getOrganizationById(user.organization_id),
    enabled: !!user?.organization_id,
  });
  const orgOptions = useMemo(
    () =>
      myOrg && !organizations.find((o) => o.id === myOrg.id)
        ? [myOrg, ...organizations]
        : organizations,
    [myOrg, organizations]
  );

  const { data: orgEvents = [], isFetched: eventsFetched } = useQuery({
    queryKey: ["org-events"],
    queryFn: () => eventsService.list(),
  });

  // Create campaign mutation
  const createMutation = useMutation({
    mutationFn: (data) => nominationsService.createCampaign(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["campaigns"] });
      toast.success(t("nominations.toasts.created"));
      setShowCreateModal(false);
    },
    onError: (error) => {
      const msg = formatError(error);
      toast.error(msg || t("nominations.toasts.create_failed"));
    },
  });

  // Toggle campaign status mutation
  const toggleStatusMutation = useMutation({
    mutationFn: ({ id, active }) => nominationsService.updateCampaignStatus(id, active),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["campaigns"] });
      toast.success(t("nominations.toasts.status_updated"));
    },
    onError: (error) => {
      toast.error(formatError(error));
    },
  });

  // End campaign mutation
  const endCampaignMutation = useMutation({
    mutationFn: (id) => nominationsService.endCampaign(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["campaigns"] });
      toast.success(t("nominations.toasts.ended"));
    },
    onError: (error) => {
      toast.error(formatError(error));
    },
  });

  // Delete campaign mutation
  const deleteMutation = useMutation({
    mutationFn: (id) => nominationsService.deleteCampaign(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["campaigns"] });
      toast.success(t("nominations.toasts.deleted"));
      setShowDetailsDrawer(false);
    },
    onError: (error) => {
      toast.error(formatError(error));
    },
  });

  // Form handling
  const { register, handleSubmit, reset, watch, formState: { errors } } = useForm({
    defaultValues: {
      name: "",
      description: "",
      start_date: "",
      end_date: "",
      is_active: false,
      scope: "global",
      organization_id: "",
      team_id: "",
      award_pool: 1000,
      budget_id: "",
      workvivo_cross_post_on_create: false,
      workvivo_enable_ongoing_posting: false,
      workvivo_default_space_id: "",
      workvivo_announcement_message: "",
      allow_multiple_nominees: false,
      event_id: "",
    },
  });

  const scope = watch("scope");
  const selectedTeamId = watch("team_id");
  const workvivoEnabled = watch("workvivo_cross_post_on_create");
  const formOrgId = watch("organization_id");

  // Budgets must belong to the campaign org; team scope also filters by team
  const filteredBudgets = useMemo(() => {
    let list = budgets;
    const campaignOrgId = isSuperAdmin ? formOrgId : user?.organization_id;
    if (campaignOrgId) {
      list = list.filter(
        (budget) => !budget.organization_id || budget.organization_id === campaignOrgId
      );
    }
    if (scope === "team" && selectedTeamId) {
      list = list.filter((budget) => budget.team_id === selectedTeamId);
    }
    return list;
  }, [budgets, isSuperAdmin, formOrgId, user?.organization_id, scope, selectedTeamId]);

  const onSubmit = (data) => {
    // Format and map fields to match backend API expectations
    const payload = {
      name: data.name,
      description: data.description || undefined,
      start_date: data.start_date,
      end_date: data.end_date,
      is_active: false,
      scope: data.scope,
      organization_id: isSuperAdmin ? (data.organization_id || undefined) : user?.organization_id,
      target_team_id: data.scope === "team" ? data.team_id : undefined,
      budget_id: data.budget_id,
      award_points: parseInt(data.award_pool, 10) || 0,
      enable_workvivo_posting: data.workvivo_enable_ongoing_posting || false,
      default_workvivo_space_id: data.workvivo_default_space_id || undefined,
      post_to_workvivo: data.workvivo_cross_post_on_create || false,
      workvivo_message: data.workvivo_announcement_message || undefined,
      allow_multiple_nominees: data.allow_multiple_nominees || false,
      event_id: data.event_id || undefined,
    };

    createMutation.mutate(payload);
  };

  const handleViewCampaign = (campaign) => {
    navigate(`/admin/campaigns/${campaign.id}`);
  };

  const handleToggleStatus = (id, currentStatus) => {
    toggleStatusMutation.mutate({ id, active: !currentStatus });
  };

  const handleEndCampaign = (id) => {
    if (window.confirm(t("nominations.confirm.end"))) {
      endCampaignMutation.mutate(id);
    }
  };

  const handleDelete = (id) => {
    if (window.confirm(t("nominations.confirm.delete"))) {
      deleteMutation.mutate(id);
    }
  };

  const openCreateModal = (prefill = {}) => {
    reset({
      name: prefill.name || "",
      description: prefill.description || "",
      start_date: new Date().toISOString().split('T')[0],
      end_date: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
      is_active: false,
      scope: "global",
      organization_id: user?.organization_id || "",
      team_id: teams[0]?.id || "",
      award_pool: 1000,
      budget_id: budgets[0]?.id || "",
      workvivo_cross_post_on_create: false,
      workvivo_enable_ongoing_posting: false,
      workvivo_default_space_id: "",
      workvivo_announcement_message: "",
      allow_multiple_nominees: false,
      event_id: prefill.event_id || "",
    });
    setShowCreateModal(true);
  };

  const [searchParams] = useSearchParams();
  const createFromEventRef = useRef(false);
  useEffect(() => {
    if (createFromEventRef.current) return;
    if (searchParams.get("createCampaign") !== "1") return;
    const eventId = searchParams.get("eventId") || "";
    if (eventId && !eventsFetched) return;
    createFromEventRef.current = true;
    openCreateModal({
      name: searchParams.get("eventName") || "",
      event_id: eventId,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams, eventsFetched]);

  const getDaysRemaining = (endDate) => {
    const end = new Date(endDate);
    const now = new Date();
    const diff = end - now;
    const days = Math.ceil(diff / (1000 * 60 * 60 * 24));
    return days;
  };

  return (
    <div className="p-3 sm:p-6 space-y-4 sm:space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-lg sm:text-2xl font-bold text-slate-800">{t("nominations.title")}</h1>
          <p className="text-xs sm:text-sm text-slate-600 mt-0.5 sm:mt-1">{t("nominations.subtitle")}</p>
        </div>
        <button
          onClick={() => openCreateModal()}
          className="flex items-center gap-2 px-4 py-2 nomination-gradient nomination-btn text-white rounded-full transition-all shadow-md"
        >
          <Plus className="w-4 h-4" />
          {t("nominations.create_campaign")}
        </button>
      </div>

      {/* Search Campaigns */}
      <div className="bg-white rounded-2xl shadow-md p-4">
        <div className="flex items-center gap-2 mb-3">
          <Search className="w-5 h-5 text-slate-600" />
          <h3 className="font-semibold text-slate-800">{t("nominations.search.title")}</h3>
        </div>
        <div className="relative">
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder={t("nominations.search.placeholder")}
            className="w-full px-4 py-2 pl-10 border border-slate-300 rounded-full focus:outline-none focus:ring-2 focus:ring-purple-500"
          />
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          {searchQuery && (
            <button
              onClick={() => setSearchQuery("")}
              className="absolute right-3 top-1/2 -translate-y-1/2 p-1 hover:bg-slate-100 rounded-full transition-colors"
            >
              <X className="w-4 h-4 text-slate-400" />
            </button>
          )}
        </div>
        {searchQuery && (
          <p className="text-sm text-slate-500 mt-2">
            {t("nominations.search.found", { count: filteredCampaigns.length, query: searchQuery })}
          </p>
        )}
      </div>

      {/* Campaigns List */}
      {campaignsLoading ? (
        <div className="flex justify-center items-center py-12">
          <div className="w-12 h-12 border-4 border-purple-200 border-t-purple-600 rounded-full animate-spin" />
        </div>
      ) : campaigns.length === 0 ? (
        <div className="bg-white rounded-2xl shadow-md p-12 text-center">
          <Award className="w-16 h-16 text-slate-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-slate-700 mb-2">{t("nominations.empty.title")}</h3>
          <p className="text-slate-500 mb-6">{t("nominations.empty.body")}</p>
          <button
            onClick={openCreateModal}
            className="px-4 py-2 nomination-gradient nomination-btn text-white rounded-full transition-all shadow-md inline-flex items-center gap-2"
          >
            <Plus className="w-4 h-4" />
            {t("nominations.create_campaign")}
          </button>
        </div>
      ) : filteredCampaigns.length === 0 ? (
        <div className="bg-white rounded-2xl shadow-md p-12 text-center">
          <Search className="w-16 h-16 text-slate-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-slate-700 mb-2">{t("nominations.empty.no_match_title")}</h3>
          <p className="text-slate-500 mb-6">{t("nominations.empty.no_match_body", { query: searchQuery })}</p>
          <button
            onClick={() => setSearchQuery("")}
            className="px-4 py-2 border border-slate-300 text-slate-700 rounded-full hover:bg-slate-50 transition-all inline-flex items-center gap-2"
          >
            <X className="w-4 h-4" />
            {t("nominations.empty.clear_search")}
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredCampaigns.map((campaign) => (
            <CampaignCard
              key={campaign.id}
              campaign={campaign}
              onView={() => handleViewCampaign(campaign)}
              onToggleStatus={handleToggleStatus}
              onEndCampaign={handleEndCampaign}
              getDaysRemaining={getDaysRemaining}
            />
          ))}
        </div>
      )}

      {/* Create Campaign Modal */}
      <AnimatePresence>
        {showCreateModal && (
          // z-40 keeps the fixed top bar (z-50) crisp above the overlay
          <div className="fixed inset-0 z-40 flex items-center justify-center p-4 pt-24">
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="absolute inset-0 bg-black/50"
              onClick={() => setShowCreateModal(false)}
            />
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              className="relative bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[calc(100vh-8rem)] overflow-y-auto overscroll-contain"
            >
              <div className="p-6 border-b border-slate-200 sticky top-0 bg-white z-10 flex justify-between items-center rounded-t-2xl">
                <h2 className="text-xl font-semibold text-slate-900">{t("nominations.form.title")}</h2>
                <button
                  onClick={() => setShowCreateModal(false)}
                  className="p-1 hover:bg-slate-100 rounded transition-colors"
                >
                  <X className="w-5 h-5 text-slate-600" />
                </button>
              </div>

              <form onSubmit={handleSubmit(onSubmit)} className="p-6 space-y-5">
                {/* Basic Info */}
                <AdminSectionCard
                  icon={Trophy}
                  title={t("nominations.form.basics")}
                  className="shadow-none border border-slate-200"
                >
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-slate-700 mb-2">
                      {t("nominations.form.name_label")} <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      {...register("name", { required: t("nominations.form.name_required") })}
                      className="w-full px-3 py-2 border border-slate-300 rounded-full focus:outline-none focus:ring-2 focus:ring-purple-500"
                      placeholder={t("nominations.form.name_placeholder")}
                    />
                    {errors.name && (
                      <p className="text-xs text-red-500 mt-1">{errors.name.message}</p>
                    )}
                  </div>

                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-slate-700 mb-2">
                      {t("nominations.form.description_label")}
                    </label>
                    <textarea
                      {...register("description")}
                      className="w-full px-3 py-2 border border-slate-300 rounded-2xl focus:outline-none focus:ring-2 focus:ring-purple-500"
                      rows="3"
                      placeholder={t("nominations.form.description_placeholder")}
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">
                      {t("nominations.form.start_date_label")} <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="date"
                      {...register("start_date", { required: t("nominations.form.start_date_required") })}
                      className="w-full px-3 py-2 border border-slate-300 rounded-full focus:outline-none focus:ring-2 focus:ring-purple-500"
                    />
                    {errors.start_date && (
                      <p className="text-xs text-red-500 mt-1">{errors.start_date.message}</p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">
                      {t("nominations.form.end_date_label")} <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="date"
                      {...register("end_date", { required: t("nominations.form.end_date_required") })}
                      className="w-full px-3 py-2 border border-slate-300 rounded-full focus:outline-none focus:ring-2 focus:ring-purple-500"
                    />
                    {errors.end_date && (
                      <p className="text-xs text-red-500 mt-1">{errors.end_date.message}</p>
                    )}
                  </div>
                </div>
                </AdminSectionCard>

                {/* Campaign Settings */}
                <AdminSectionCard
                  icon={Target}
                  title={t("nominations.form.settings_title")}
                  description={t("nominations.form.settings_description")}
                  className="shadow-none border border-slate-200"
                >
                  <div className="space-y-4">
                    {isSuperAdmin && (
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-2">
                        {t("nominations.form.organization_label")} <span className="text-red-500">*</span>
                      </label>
                      <select
                        {...register("organization_id", { required: isSuperAdmin ? t("nominations.form.organization_required") : false })}
                        className="w-full px-3 py-2 border border-slate-300 rounded-full focus:outline-none focus:ring-2 focus:ring-purple-500"
                      >
                        <option value="">{t("nominations.form.select_organization")}</option>
                        {orgOptions.map((org) => (
                          <option key={org.id} value={org.id}>
                            {org.name}
                          </option>
                        ))}
                      </select>
                      {errors.organization_id && (
                        <p className="text-xs text-red-500 mt-1">{errors.organization_id.message}</p>
                      )}
                    </div>
                    )}

                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-2">
                        {t("nominations.form.scope_label")} <span className="text-red-500">*</span>
                      </label>
                      <select
                        {...register("scope")}
                        className="w-full px-3 py-2 border border-slate-300 rounded-full focus:outline-none focus:ring-2 focus:ring-purple-500"
                      >
                        <option value="global">{t("nominations.form.scope_global")}</option>
                        <option value="team">{t("nominations.form.scope_team")}</option>
                      </select>
                    </div>

                    {scope === "team" && (
                      <div>
                        <label className="block text-sm font-medium text-slate-700 mb-2">
                          {t("nominations.form.team_label")} <span className="text-red-500">*</span>
                        </label>
                        <select
                          {...register("team_id", {
                            required: scope === "team" ? t("nominations.form.team_required") : false
                          })}
                          className="w-full px-3 py-2 border border-slate-300 rounded-full focus:outline-none focus:ring-2 focus:ring-purple-500"
                        >
                          <option value="">{t("nominations.form.select_team")}</option>
                          {teams.map((team) => (
                            <option key={team.id} value={team.id}>
                              {team.name}
                            </option>
                          ))}
                        </select>
                        {errors.team_id && (
                          <p className="text-xs text-red-500 mt-1">{errors.team_id.message}</p>
                        )}
                      </div>
                    )}

                    {/* Points come from a budget, so the pool and its source
                        read as one decision. */}
                    <div className="pt-4 border-t border-slate-200">
                      <label className="block text-sm font-medium text-slate-700 mb-2">
                        {t("nominations.form.award_pool_label")} <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="number"
                        step="0.01"
                        {...register("award_pool", {
                          required: t("nominations.form.award_pool_required"),
                          min: { value: 1, message: t("nominations.form.award_pool_min") }
                        })}
                        className="w-full px-3 py-2 border border-slate-300 rounded-full focus:outline-none focus:ring-2 focus:ring-purple-500"
                        placeholder="1000"
                      />
                      {errors.award_pool && (
                        <p className="text-xs text-red-500 mt-1">{errors.award_pool.message}</p>
                      )}
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-2">
                        {t("nominations.form.budget_label")} <span className="text-red-500">*</span>
                      </label>
                      <select
                        {...register("budget_id", { required: t("nominations.form.budget_required") })}
                        className="w-full px-3 py-2 border border-slate-300 rounded-full focus:outline-none focus:ring-2 focus:ring-purple-500"
                      >
                        <option value="">{t("nominations.form.select_budget")}</option>
                        {filteredBudgets.map((budget) => (
                          <option key={budget.id} value={budget.id}>
                            {t("nominations.form.budget_option", {
                              name: budget.name,
                              amount: fmt.number(Math.floor(budget.available_amount || 0)),
                            })}
                          </option>
                        ))}
                      </select>
                      {filteredBudgets.length === 0 && scope === "team" && selectedTeamId && (
                        <p className="text-xs text-amber-600 mt-1">{t("nominations.form.no_team_budget")}</p>
                      )}
                      {errors.budget_id && (
                        <p className="text-xs text-red-500 mt-1">{errors.budget_id.message}</p>
                      )}
                    </div>

                    <div className="pt-4 border-t border-slate-200">
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="checkbox"
                          {...register("allow_multiple_nominees")}
                          className="h-4 w-4 rounded border-slate-300 text-purple-600 focus:ring-purple-500"
                        />
                        <span className="text-sm font-medium text-slate-700">
                          {t("nominations.form.multiple_nominees_label")}
                        </span>
                      </label>
                      <p className="text-xs text-slate-500 mt-1 ml-6">
                        {t("nominations.form.multiple_nominees_hint")}
                      </p>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-2">
                        {t("nominations.form.linked_event_label")}
                      </label>
                      <select
                        {...register("event_id")}
                        className="w-full px-3 py-2 border border-slate-300 rounded-full focus:outline-none focus:ring-2 focus:ring-purple-500"
                      >
                        <option value="">{t("nominations.form.no_linked_event")}</option>
                        {orgEvents.map((event) => (
                          <option key={event.id} value={event.id} disabled={!!event.campaign_id}>
                            {event.campaign_id
                              ? t("nominations.form.event_option_linked", { name: event.name, date: event.event_date })
                              : t("nominations.form.event_option", { name: event.name, date: event.event_date })}
                          </option>
                        ))}
                      </select>
                      <p className="text-xs text-slate-500 mt-1">
                        {t("nominations.form.linked_event_hint")}
                      </p>
                    </div>
                  </div>
                </AdminSectionCard>

                {/* Workvivo Integration */}
                <AdminSectionCard
                  icon={Globe}
                  title={t("nominations.form.workvivo_title")}
                  description={t("nominations.form.workvivo_description")}
                  className="shadow-none border border-slate-200"
                >
                  <div className="space-y-4">
                    <div className="flex items-center">
                      <input
                        type="checkbox"
                        id="workvivo_cross_post_on_create"
                        {...register("workvivo_cross_post_on_create")}
                        className="h-4 w-4 text-purple-600 focus:ring-purple-500 border-slate-300 rounded"
                      />
                      <label htmlFor="workvivo_cross_post_on_create" className="ml-2 text-sm text-slate-700">
                        {t("nominations.form.cross_post_label")}
                      </label>
                    </div>

                    {workvivoEnabled && (
                      <>
                        <div className="flex items-center">
                          <input
                            type="checkbox"
                            id="workvivo_enable_ongoing_posting"
                            {...register("workvivo_enable_ongoing_posting")}
                            className="h-4 w-4 text-purple-600 focus:ring-purple-500 border-slate-300 rounded"
                          />
                          <label htmlFor="workvivo_enable_ongoing_posting" className="ml-2 text-sm text-slate-700">
                            {t("nominations.form.ongoing_posting_label")}
                          </label>
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-slate-700 mb-2">
                            {t("nominations.form.space_id_label")}
                          </label>
                          <input
                            type="text"
                            {...register("workvivo_default_space_id")}
                            className="w-full px-3 py-2 border border-slate-300 rounded-full focus:outline-none focus:ring-2 focus:ring-purple-500"
                            placeholder={t("nominations.form.space_id_placeholder")}
                          />
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-slate-700 mb-2">
                            {t("nominations.form.announcement_label")}
                          </label>
                          <textarea
                            {...register("workvivo_announcement_message")}
                            className="w-full px-3 py-2 border border-slate-300 rounded-2xl focus:outline-none focus:ring-2 focus:ring-purple-500"
                            rows="3"
                            placeholder={t("nominations.form.announcement_placeholder")}
                          />
                        </div>
                      </>
                    )}
                  </div>
                </AdminSectionCard>

                {/* Actions */}
                <div className="flex gap-3 pt-1">
                  <button
                    type="button"
                    onClick={() => setShowCreateModal(false)}
                    className="flex-1 px-4 py-2 border border-slate-300 text-slate-700 rounded-full hover:bg-slate-50 transition-colors"
                  >
                    {t("common:actions.cancel")}
                  </button>
                  <button
                    type="submit"
                    disabled={createMutation.isPending}
                    className="flex-1 px-4 py-2 nomination-gradient nomination-btn text-white rounded-full transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                  >
                    {createMutation.isPending ? (
                      <>
                        <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                        {t("nominations.form.creating")}
                      </>
                    ) : (
                      <>
                        <Plus className="w-4 h-4" />
                        {t("nominations.create_campaign")}
                      </>
                    )}
                  </button>
                </div>
              </form>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* Campaign Details Drawer */}
      <AnimatePresence>
        {showDetailsDrawer && selectedCampaign && (
          <div className="fixed inset-0 z-40 flex justify-end pt-20">
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="absolute inset-0 bg-black/50"
              onClick={() => setShowDetailsDrawer(false)}
            />
            <motion.div
              initial={{ x: "100%" }}
              animate={{ x: 0 }}
              exit={{ x: "100%" }}
              transition={{ type: "spring", damping: 25 }}
              className="relative bg-white w-full max-w-2xl h-full overflow-y-auto shadow-2xl"
            >
              <div className="p-6 border-b border-slate-200 sticky top-0 bg-white z-10">
                <div className="flex justify-between items-start">
                  <div>
                    <h2 className="text-xl font-semibold text-slate-900">{selectedCampaign.name}</h2>
                    <p className="text-sm text-slate-600 mt-1">{t("nominations.drawer.campaign_id", { id: selectedCampaign.id })}</p>
                  </div>
                  <button
                    onClick={() => setShowDetailsDrawer(false)}
                    className="p-1 hover:bg-slate-100 rounded transition-colors"
                  >
                    <X className="w-5 h-5 text-slate-600" />
                  </button>
                </div>
              </div>

              <div className="p-6 space-y-6">
                {/* Campaign Info */}
                <div className="space-y-4">
                  <div className="p-4 bg-slate-50 rounded-2xl">
                    <p className="text-sm text-slate-700">{selectedCampaign.description || t("nominations.drawer.no_description")}</p>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div className="p-4 bg-blue-50 rounded-2xl border border-blue-200">
                      <div className="flex items-center gap-2 mb-1">
                        <Calendar className="w-4 h-4 text-blue-600" />
                        <p className="text-xs text-blue-700 font-medium">{t("nominations.drawer.duration")}</p>
                      </div>
                      <p className="text-sm text-blue-900">
                        {t("nominations.drawer.date_range", {
                          start: fmt.date(selectedCampaign.start_date),
                          end: fmt.date(selectedCampaign.end_date),
                        })}
                      </p>
                    </div>

                    <div className="p-4 bg-green-50 rounded-2xl border border-green-200">
                      <div className="flex items-center gap-2 mb-1">
                        <Award className="w-4 h-4 text-green-600" />
                        <p className="text-xs text-green-700 font-medium">{t("nominations.drawer.award_points")}</p>
                      </div>
                      <p className="text-sm text-green-900 font-semibold">{t("nominations.drawer.points", { points: fmt.number(selectedCampaign.award_points) })}</p>
                      <p className="text-xs text-green-600 mt-1">
                        {t("nominations.drawer.points_remaining", {
                          points: fmt.number((selectedCampaign.award_points || 0) - (selectedCampaign.points_used || 0)),
                        })}
                      </p>
                    </div>

                    <div className="p-4 bg-purple-50 rounded-2xl border border-purple-200">
                      <div className="flex items-center gap-2 mb-1">
                        <Target className="w-4 h-4 text-purple-600" />
                        <p className="text-xs text-purple-700 font-medium">{t("nominations.drawer.scope")}</p>
                      </div>
                      <p className="text-sm text-purple-900 capitalize">{scopeLabels[selectedCampaign.scope] || selectedCampaign.scope}</p>
                    </div>

                    <div className="p-4 bg-amber-50 rounded-2xl border border-amber-200">
                      <div className="flex items-center gap-2 mb-1">
                        <Trophy className="w-4 h-4 text-amber-600" />
                        <p className="text-xs text-amber-700 font-medium">{t("nominations.drawer.status")}</p>
                      </div>
                      <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${
                        selectedCampaign.is_active
                          ? "bg-green-100 text-green-700"
                          : "bg-slate-100 text-slate-700"
                      }`}>
                        {selectedCampaign.is_active ? t("common:status.active") : t("common:status.inactive")}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Nominations List */}
                <div>
                  <h3 className="text-lg font-semibold text-slate-800 mb-3">{t("nominations.drawer.nominations")}</h3>
                  <div className="p-8 bg-slate-50 rounded-2xl border border-slate-200 text-center">
                    <p className="text-sm text-slate-600">{t("nominations.drawer.coming_soon")}</p>
                  </div>
                </div>

                {/* Actions */}
                <div className="flex gap-3 pt-4 border-t border-slate-200">
                  <button
                    onClick={() => handleToggleStatus(selectedCampaign.id, selectedCampaign.is_active)}
                    disabled={toggleStatusMutation.isPending}
                    className="flex-1 px-4 py-2 border border-slate-300 text-slate-700 rounded-full hover:bg-slate-50 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
                  >
                    {selectedCampaign.is_active ? (
                      <>
                        <Pause className="w-4 h-4" />
                        {t("nominations.drawer.pause")}
                      </>
                    ) : (
                      <>
                        <Play className="w-4 h-4" />
                        {t("nominations.drawer.activate")}
                      </>
                    )}
                  </button>
                  <button
                    onClick={() => handleDelete(selectedCampaign.id)}
                    disabled={deleteMutation.isPending}
                    className="px-4 py-2 bg-red-600 text-white rounded-full hover:bg-red-700 transition-colors disabled:opacity-50 flex items-center gap-2"
                  >
                    <Trash2 className="w-4 h-4" />
                    {t("common:actions.delete")}
                  </button>
                </div>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </div>
  );
};

// Campaign Card Component
const CampaignCard = ({ campaign, onView, onToggleStatus, onEndCampaign, getDaysRemaining }) => {
  const { t } = useTranslation("admin");
  const fmt = useFormat();
  const daysRemaining = getDaysRemaining(campaign.end_date);
  const isActive = campaign.is_active;
  const isEnded = daysRemaining < 0;
  const isFinalized = !!campaign.winner_awarded_at;
  const status = campaign.status || (isActive ? "active" : "inactive");
  const isDraft = status === "draft" || (!isActive && status !== "completed");
  const hasNoAwards = isDraft && (campaign.awards_count ?? 0) === 0;

  const scopeLabels = {
    global: t("nominations.card.scope_global"),
    team: t("nominations.card.scope_team"),
  };
  const scopeText =
    campaign.scope === "team" && campaign.target_team_name
      ? t("nominations.card.team_scope", { name: campaign.target_team_name })
      : scopeLabels[campaign.scope] || t("nominations.card.scope_generic", { scope: campaign.scope });

  return (
    <motion.div
      whileHover={{ y: -2 }}
      className="bg-white rounded-2xl shadow-md overflow-hidden border border-slate-200 hover:shadow-lg transition-all flex flex-col"
    >
      <div className="p-5 flex flex-col flex-1">
        <div className="flex items-start justify-between mb-3">
          <div className="flex-1 min-w-0">
            <h3 className="text-lg font-semibold text-slate-900 truncate">{campaign.name}</h3>
            <p className="text-xs text-slate-600 mt-1 line-clamp-2">{campaign.description || t("nominations.card.no_description")}</p>
          </div>
          <div className="flex flex-col items-end gap-1 ml-2 flex-shrink-0">
            <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
              isActive
                ? "bg-green-100 text-green-700"
                : status === "completed"
                ? "bg-blue-100 text-blue-700"
                : "bg-slate-100 text-slate-700"
            }`}>
              {status === "completed" ? t("nominations.card.status_completed") : isActive ? t("nominations.card.status_active") : t("nominations.card.status_draft")}
            </span>
            {isFinalized && (
              <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-700">
                {t("nominations.card.finalized")}
              </span>
            )}
          </div>
        </div>

        {/* The award pool is the number an admin scans for, so it leads;
            dates and scope read as supporting detail rather than peers. */}
        <div className="mb-4 pt-3 border-t border-slate-100">
          <div className="flex items-center gap-2 mb-2">
            <Award className="w-4 h-4 text-slate-400 flex-shrink-0" />
            <span className="text-base font-semibold text-slate-900">
              {fmt.number(campaign.award_points)}
            </span>
            <span className="text-xs text-slate-500">{t("nominations.card.award_pool")}</span>
          </div>
          <div className="space-y-1 ml-6">
            <div className="flex items-center gap-2 text-xs text-slate-500">
              <Calendar className="w-3.5 h-3.5 flex-shrink-0" />
              <span>{t("nominations.drawer.date_range", { start: fmt.date(campaign.start_date), end: fmt.date(campaign.end_date) })}</span>
            </div>
            <div className="flex items-center gap-2 text-xs text-slate-500">
              <Users className="w-3.5 h-3.5 flex-shrink-0" />
              <span className="capitalize truncate">{scopeText}</span>
            </div>
          </div>
        </div>

        {isDraft && hasNoAwards && (
          <div className="mb-4 p-2 bg-amber-50 rounded-2xl border border-amber-200">
            <p className="text-xs text-amber-700 text-center">
              <Trophy className="w-3 h-3 inline mr-1" />
              {t("nominations.card.add_award_first")}
            </p>
          </div>
        )}

        {!isEnded && !isDraft && (
          <div className="mb-4 p-2 bg-blue-50 rounded-2xl border border-blue-200">
            <p className="text-xs text-blue-700 text-center">
              <Clock className="w-3 h-3 inline mr-1" />
              {t("nominations.card.days_remaining", { count: daysRemaining })}
            </p>
          </div>
        )}

        <div className="flex gap-2 mt-auto">
          <button
            onClick={onView}
            className="flex-1 px-3 py-2 nomination-gradient nomination-btn text-white rounded-full transition-all text-sm flex items-center justify-center gap-2"
          >
            <Eye className="w-4 h-4" />
            {t("nominations.card.view_details")}
          </button>
          {(status === "active" || status === "paused") && !isFinalized && (
            <button
              onClick={() => onEndCampaign(campaign.id)}
              className="px-3 py-2 border border-orange-300 text-orange-700 rounded-full hover:bg-orange-50 transition-colors text-sm"
              title={t("nominations.card.end_campaign")}
            >
              <StopCircle className="w-4 h-4" />
            </button>
          )}
          {isDraft ? (
            <button
              onClick={() => !hasNoAwards && onToggleStatus(campaign.id, false)}
              disabled={hasNoAwards}
              title={hasNoAwards ? t("nominations.card.add_award_first") : t("nominations.card.activate")}
              className={`px-3 py-2 rounded-full text-sm flex items-center gap-1 transition-colors ${
                hasNoAwards
                  ? "border border-slate-200 text-slate-300 cursor-not-allowed"
                  : "border border-green-300 text-green-700 hover:bg-green-50"
              }`}
            >
              <Play className="w-4 h-4" />
            </button>
          ) : isActive ? (
            <button
              onClick={() => onToggleStatus(campaign.id, true)}
              className="px-3 py-2 border border-slate-300 text-slate-700 rounded-full hover:bg-slate-50 transition-colors text-sm"
              title={t("nominations.card.pause")}
            >
              <Pause className="w-4 h-4" />
            </button>
          ) : null}
        </div>
      </div>
    </motion.div>
  );
};

export default NominationsSection;
